import React from 'react';
import {Box, Button, SideNavigation, Spinner} from '@cloudscape-design/components';
import {Chat} from './cassandra-api';
import './cassandra.css';

interface ChatPanelProps {
    chats: Chat[];
    activeChatId: string | null;
    loading: boolean;
    onSelectChat: (chatId: string) => void;
    onNewChat: () => void;
    onDeleteChat: (chatId: string) => void;
}

function formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return d.toLocaleDateString();
}

const NAV_ITEMS = [
    {type: 'link' as const, text: 'Chats', href: '/ask-excelsior'},
    {type: 'link' as const, text: 'Knowledge', href: '/ask-excelsior/knowledge'},
];

export default function ChatPanel({chats, activeChatId, loading, onSelectChat, onNewChat, onDeleteChat}: ChatPanelProps) {
    return (
        <div className="cassandra-chat-panel">
            <div className="cassandra-sidebar-logo">
                <span className="cassandra-sidebar-logo-icon">✦</span>
                <span className="cassandra-sidebar-logo-text">Ask Excelsior</span>
            </div>

            <Button variant="primary" fullWidth onClick={onNewChat} iconName="add-plus">
                New Chat
            </Button>

            <div className="cassandra-sidebar-nav">
                <SideNavigation
                    activeHref={'/ask-excelsior'}
                    items={NAV_ITEMS}
                    onFollow={(e) => {
                        e.preventDefault();
                    }}
                />
            </div>

            <hr className="cassandra-divider" />

            <Box color="text-label" fontSize="body-s">
                Recent chats
            </Box>

            <div className="cassandra-chat-list-section">
                {loading ? (
                    <Box textAlign="center" padding="l">
                        <Spinner />
                    </Box>
                ) : chats.length === 0 ? (
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                        No conversations yet
                    </Box>
                ) : (
                    <ul className="cassandra-chat-list">
                        {chats.map((chat) => {
                            const isActive = activeChatId === chat.id;
                            return (
                                <li key={chat.id} className="cassandra-chat-item">
                                    <div
                                        className={`cassandra-chat-item-link${isActive ? ' cassandra-chat-item-link--active' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Select chat: ${chat.title}`}
                                        onClick={() => onSelectChat(chat.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') onSelectChat(chat.id);
                                        }}
                                    >
                                        <div className="cassandra-chat-item-content">
                                            <div className="cassandra-chat-item-title">{chat.title || 'Untitled'}</div>
                                            <div className="cassandra-chat-item-timestamp">{formatDate(chat.updatedAt)}</div>
                                        </div>
                                        <button
                                            className="cassandra-chat-delete"
                                            aria-label={`Delete chat: ${chat.title}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDeleteChat(chat.id);
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
